import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@vd/ui';
import { useTheme } from '../app/theme';

/**
 * Page Chrome opens automatically when the user uninstalls the
 * extension (chrome.runtime.setUninstallURL). Asks one question:
 * why did you stop using it. Reason + optional comment + optional
 * email for follow-up. Anonymous unless the user volunteers an
 * email.
 *
 * The version is read off the ?v= query the SW appends to the
 * uninstall URL. Used only to tag the feedback message — never
 * stored on its own, never used for tracking.
 */

const REASONS: Array<{ value: string; label: string }> = [
  { value: 'expectations', label: "It didn't do what I expected" },
  { value: 'setup', label: 'It was hard to set up or connect' },
  { value: 'alternative', label: 'I found a different tool' },
  { value: 'temporary', label: 'I was just trying it out' },
  { value: 'bug', label: 'I hit a bug or it stopped working' },
  { value: 'other', label: 'Other' },
];

type FormState = 'idle' | 'submitting' | 'sent' | 'error';

export function UninstallFeedbackPage() {
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const version = params.get('v') ?? '';

  const [reason, setReason] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [state, setState] = useState<FormState>('idle');
  const [errorText, setErrorText] = useState<string>('');

  const logoSrc = theme === 'dark' ? '/toado%20logo%20white.svg' : '/toado%20logo.svg';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason && !comment.trim()) return; // empty submit, do nothing
    setState('submitting');
    setErrorText('');
    try {
      const apiBase = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
      const res = await fetch(`${apiBase}/api/v1/feedback/uninstall`, {
        method: 'POST',
        // Public, and nobody's session: it is sent without one.
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, comment: comment.trim(), email: email.trim(), version }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: res.statusText }));
        setErrorText(body.message ?? 'Could not submit feedback.');
        setState('error');
        return;
      }
      setState('sent');
    } catch (err) {
      setErrorText((err as Error).message);
      setState('error');
    }
  }

  return (
    <div style={shellStyle}>
      <img src={logoSrc} alt="Toado" style={{ height: 28, marginBottom: 24 }} />

      {state === 'sent' ? (
        <div style={cardStyle}>
          <h1 style={titleStyle}>Thanks for the feedback</h1>
          <p style={bodyStyle}>
            We read every response. If you left an email, we may follow up
            with one quick question, no marketing.
          </p>
          <p style={bodyStyle}>
            Want to come back? Reinstall from{' '}
            <a href="https://www.toado.dev" style={{ color: 'var(--accent)' }}>toado.dev</a>{' '}
            anytime.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} style={cardStyle}>
          <h1 style={titleStyle}>Sorry to see you go</h1>
          <p style={bodyStyle}>
            We'd love to know what didn't click. Anything you tell us shapes
            the next version. Skip any field you'd rather not answer.
          </p>

          <fieldset style={{ border: 'none', padding: 0, margin: '20px 0 0' }}>
            <legend style={legendStyle}>Why did you uninstall?</legend>
            {REASONS.map((r) => (
              <label key={r.value} style={radioLabelStyle}>
                <input
                  type="radio"
                  name="reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ marginRight: 8 }}
                />
                {r.label}
              </label>
            ))}
          </fieldset>

          <label style={{ ...legendStyle, display: 'block', marginTop: 20 }}>
            Anything else? (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="A specific bug, a missing feature, a workflow we didn't fit..."
            rows={4}
            maxLength={2000}
            style={textareaStyle}
          />

          <label style={{ ...legendStyle, display: 'block', marginTop: 16 }}>
            Email (optional, only used to follow up)
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={inputStyle}
          />

          {errorText && (
            <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 12 }}>
              {errorText}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <Button
              type="submit"
              variant="primary"
              disabled={state === 'submitting' || (!reason && !comment.trim())}
            >
              {state === 'submitting' ? 'Sending…' : 'Send feedback'}
            </Button>
          </div>

          <p style={{ ...bodyStyle, fontSize: 11, color: 'var(--text-tertiary)', marginTop: 20 }}>
            Submitting this sends what you typed to the Toado team. Nothing
            else is collected, no IP, no browser fingerprint, no captures.
          </p>
        </form>
      )}
    </div>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 24px',
  background: 'var(--bg)',
  fontFamily: 'Inter, sans-serif',
  color: 'var(--text)',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 520,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 24,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  margin: '0 0 8px',
};

const bodyStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-secondary)',
  margin: '0 0 8px',
  lineHeight: 1.5,
};

const legendStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text)',
  marginBottom: 8,
};

const radioLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: 14,
  color: 'var(--text)',
  padding: '6px 0',
  cursor: 'pointer',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 14,
  color: 'var(--text)',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '10px 12px',
  resize: 'vertical',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 14,
  color: 'var(--text)',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '10px 12px',
};
